import { format } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { STICKY_HEADERS } from "@/constants/featureFlags";
import { useCalendar } from "@/contexts/calendarContext";
import { useMember } from "@/contexts/memberContext";
import { useElementWidth } from "@/hooks/useElementWidth";
import { TimeSlotClickInterceptor } from "@/interceptors/booking";
import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import { occursOnDate } from "@/lib/recurrence";
import { sameUtcDay, utcDayBounds } from "@/lib/calendarDay";
import { AmenityService } from "@/services/AmenityService";
import { filterRoomsByAmenities } from "@/utils/roomAmenityFilter";
import { MakeABookingDialog } from "../../MakeABookingDialog";
import { AmenityBookingColumn } from "./BookingColumn";
import { dayViewColumnLayout, formatDayHeader, isSameDay, layoutBookingTile } from "./BrickWeekSchedule.utils";
import Bubble from "./Bubble";
import Buffers from "./Buffers";
import { hasAnyBuffer } from "@/utils/bufferFlags";
import { AmenityColumnHeaders, RoomColumnHeaders } from "./ColumnHeaders";
import RoomDialog from "./RoomDialog";
import type { BookingInterceptionResult } from "@/interceptors/booking";
import type { AmenityBooking } from "@/types/AmenityBooking";
import type { Room } from "@/types/Room";

const BrickDayScheduleAlt = () => {
  const { member } = useMember();
  const KEY_WIDTH = 60; //px
  const KEY_HEIGHT = 36;
  const TIMES_TO_HIDE_EARLY = ["0030", "0100", "0130", "0200", "0230", "0300", "0330", "0400", "0430", "0500", "0530"];

  const { isLoading, bookings, date, selectedLocation, selectedRooms, selectedAmenities, selectedRoomAmenities, refreshBookings } =
    useCalendar();
  const [amenityBookings, setAmenityBookings] = useState<AmenityBooking[]>([]);
  const [elementRef, elementWidth] = useElementWidth<HTMLDivElement>();
  const [hiddenEarly, setHiddenEarly] = useState(true);

  // The "Rooms with amenities" filter narrows what the calendar draws to rooms having ALL selected
  // amenities (empty selection = all selected rooms).
  const visibleRooms = filterRoomsByAmenities(selectedRooms, selectedRoomAmenities);

  // Columns never shrink below MIN_COLUMN_WIDTH_PX (where ColumnHeaders drops the room name):
  // a location with more rooms than fit scrolls horizontally, with the sticky room header kept in
  // step. `elementWidth` is measured by the sizer inside the key-excluded area, so the time key
  // must NOT be subtracted again (doing so dropped the header to 32px and clipped vertical names).
  const { contentWidthPx, colouredHeaderHeightPx } = dayViewColumnLayout(
    elementWidth,
    visibleRooms.length + selectedAmenities.length,
  );
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const handleGridScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };
  const [showRoomInfoModal, setShowRoomInfoModal] = useState<boolean>(false);
  const [roomForInfoModal, setRoomForInfoModal] = useState<Room | null>(null);
  const [showBookingDialog, setShowBookingDialog] = useState<boolean>(false);
  const [bookingResult, setBookingResult] = useState<BookingInterceptionResult | null>(null);
  // UTC-anchored: `date` may be a UTC-midnight instant (e.g. parsed from the URL date param); a
  // `toDateString()` (local) comparison against `new Date()` can disagree with the grid's own UTC
  // day under a non-UTC viewer TZ.
  const isToday = date ? sameUtcDay(date, new Date()) : false;

  // Helper to check if a specific time slot is shuttered
  const isTimeSlotShuttered = useCallback((room: Room, slotDate: Date, time: string): boolean => {
    if (!room.shutterUntil) return false;
    const shutterDate = new Date(room.shutterUntil);

    // Check if shutterDate is valid
    if (isNaN(shutterDate.getTime())) {
      console.error("[isTimeSlotShuttered] Invalid shutterUntil date:", room.shutterUntil);
      return false;
    }

    // Parse the time string (HHmm format)
    const hours = parseInt(time.substring(0, 2), 10);
    const minutes = parseInt(time.substring(2, 4), 10);
    const slotDateTime = new Date(slotDate);
    slotDateTime.setHours(hours, minutes, 0, 0);

    // Check if slotDateTime is valid
    if (isNaN(slotDateTime.getTime())) {
      console.error("[isTimeSlotShuttered] Invalid slot date/time:", slotDate, time);
      return false;
    }

    // Time slot is shuttered if it's before the shutterUntil datetime
    const isShuttered = slotDateTime < shutterDate;

    // Debug logging - only log when shuttered to avoid console spam
    if (isShuttered) {
      console.log("[isTimeSlotShuttered] SHUTTERED", {
        roomName: room.name,
        shutterUntil: room.shutterUntil,
        shutterDate: shutterDate.toISOString(),
        slotDateTime: slotDateTime.toISOString(),
      });
    }

    return isShuttered;
  }, []);

  // Generate 30-minute segments for a 24-hour day
  const segments: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
      segments.push(time);
    }
  }

  if (isLoading) {
    return null;
  }

  // we always return the width of the element on day because we don't have
  // other columns
  function dynamicDayWidth(): number {
    return elementWidth; //fits on mobile?
  }

  const handleShowRoomInfoModal = (room: Room) => {
    console.log("handleShowRoomInfoModal", room);
    setRoomForInfoModal(room);
    setShowRoomInfoModal(!showRoomInfoModal);
  };

  // const handleBookingSuccess = (booking?: Booking) => {
  //   if (!booking) return;

  //   setCreatedBooking(booking);
  //   setShowBookingSuccessDialog(true);
  // };

  // Load amenity bookings for the current day
  useEffect(() => {
    if (!selectedLocation || !date) return;

    // UTC day bounds, not local setHours(0/23:59) — `date` represents a UTC calendar day, and
    // local-clock bounds would shift under a non-UTC viewer TZ (the range sent to the amenity
    // API would cover the wrong day, hiding/duplicating amenity bookings on the grid).
    const { start: startDate, end: endDate } = utcDayBounds(date);

    const amenityService = new AmenityService();
    amenityService
      .getAmenityBookingsForLocationAndDateRange(selectedLocation.id, startDate, endDate)
      .then((amenityBookingsResult: AmenityBooking[]) => {
        setAmenityBookings(amenityBookingsResult);
      });
    // `bookings` in the deps so amenity tiles re-fetch whenever the room bookings refresh (e.g. after
    // make-repeat / remove-repeat). Without it a removed recurrence's amenity kept repeating until the
    // user navigated away. (`bookings` is React-Query data — stable ref until a refetch.)
  }, [selectedLocation, date, bookings]);

  const handleHideEarlySegments = () => {
    setHiddenEarly(!hiddenEarly);
  };

  const handleTimeSlotClick = (event: React.MouseEvent, time: string, room?: Room | null) => {
    if (!member || !date) return;

    console.log("[BrickDayScheduleAlt] handleTimeSlotClick - bookings from context:", {
      bookingsType: typeof bookings,
      bookingsIsNull: bookings === null,
      bookingsIsArray: Array.isArray(bookings),
      bookingsLength: bookings?.length ?? "N/A",
      date: date.toISOString(),
      time,
      roomId: room?.id,
    });

    const result = TimeSlotClickInterceptor({
      event,
      date,
      time,
      member,
      location: selectedLocation || null,
      roomId: room?.id,
      room,
      bookings,
    });
    // Add room name to the result if provided
    if (result.details && room?.name) {
      result.details.roomName = room.name;
    }

    console.log("[BrickDayScheduleAlt] TimeSlotClickInterceptor result:", {
      status: result.status,
      bookingsInResult: result.bookings?.length ?? "N/A",
    });

    setBookingResult(result);
    setShowBookingDialog(true);
  };

  return (
    <>
      {/* <div className="absolute top-4 right-4 bg-indigo-600 text-white text-xs p-2 rounded">
        {elementWidth}px ({Math.round(elementWidth)}px) (DDW: {dynamicDayWidth()}px)
      </div> */}
      <div className="flex flex-col gap-4 p-2">
        <div className={`flex flex-col border rounded-lg ${STICKY_HEADERS ? "" : "overflow-hidden"}`}>
          <div className="flex flex-row justify-between bg-gray-200 dark:bg-slate-800 p-2 h-2 items-center">&nbsp;</div>

          {/* Sticky header: date + room column headers */}
          <div
            className={STICKY_HEADERS ? "sticky top-0 z-40" : ""}
            id="room-schedule-sticky-header"
            data-testid="room-schedule-sticky-header"
          >
            <div className="flex flex-row w-full">
              <div
                className="text-center border-r border-b bg-gray-50 dark:bg-slate-950 dark:border-slate-800 border-gray-200 h-16 pt-4.5"
                style={{ minWidth: `${KEY_WIDTH}px`, maxWidth: `${KEY_WIDTH}px`, width: `${KEY_WIDTH}px` }}
              >
                Time
              </div>
              <div style={{ width: `calc(100% - ${KEY_WIDTH}px)` }}>
                <div className="h-0 bg-indigo-700 w-full" ref={elementRef} data-testid="room-schedule-sizer"></div>
                <div data-testid="room-schedule-day-headers" className="flex h-16">
                  <div
                    className={`w-full pt-4.5 border-r border-b bg-gray-50 border-gray-200 dark:bg-slate-900 dark:border-slate-800 text-center ${
                      isToday ? "tpr-background px-1 rounded-sm text-white" : ""
                    }`}
                    data-testid={`room-schedule-day-header-${date ? format(date, "yyyy-MM-dd") : ""}`}
                  >
                    {formatDayHeader(date ?? new Date())}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row w-full">
              <div
                className="bg-gray-50 dark:bg-slate-950"
                style={{
                  minWidth: `${KEY_WIDTH}px`,
                  maxWidth: `${KEY_WIDTH}px`,
                  width: `${KEY_WIDTH}px`,
                  height: `${colouredHeaderHeightPx}px`,
                }}
              >
                &nbsp;
              </div>
              <div
                className="flex-1 min-w-0"
                style={{ height: `${colouredHeaderHeightPx}px` }}
                data-testid="room-schedule-coloured-room-headers"
              >
                {/* Clipped strip whose scrollLeft follows the grid below. It lives outside the
                    grid's horizontal scroller so the header stays sticky on vertical page scroll. */}
                <div ref={headerScrollRef} className="overflow-hidden" data-testid="room-schedule-coloured-room-headers-scroll">
                  <div
                    // Solid backing so the (sometimes translucent) room-header "shoulder" colours
                    // composite over white instead of letting scrolled bookings show through the
                    // sticky header. See paletteBySwatch — custom swatches use /30-opacity shoulders.
                    className="flex flex-row justify-evenly gap-0 bg-white dark:bg-slate-900"
                    style={{
                      height: `${colouredHeaderHeightPx}px`,
                      width: contentWidthPx ? `${contentWidthPx}px` : undefined,
                    }}
                    data-testid="room-schedule-coloured-room-headers-track"
                  >
                    <RoomColumnHeaders
                      rooms={visibleRooms}
                      date={date || new Date()}
                      onRoomClick={handleShowRoomInfoModal}
                    />
                    <AmenityColumnHeaders amenities={selectedAmenities} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule body: time key + booking columns */}
          <div className="flex flex-row w-full">
            <div data-testid="room-schedule-key-column">
              <div className="flex flex-col">
                {segments.map((time, i) => {
                  if (hiddenEarly && TIMES_TO_HIDE_EARLY.includes(time)) {
                    return null;
                  }
                  // Darker rule at each hour boundary; hour label rendered as a small pill
                  // straddling the rule, no half-hour label at all.
                  const isHalfHour = time.endsWith("30");
                  const borderClasses = isHalfHour
                    ? "border-b border-gray-400 dark:border-slate-500"
                    : "border-b border-gray-200 dark:border-slate-700 border-dotted";
                  const showHourBubble = !isHalfHour && time !== "0000";
                  return (
                    <div
                      key={i}
                      data-time={time}
                      className={`${borderClasses} relative flex items-center justify-center text-xs bg-gray-50 dark:bg-slate-900 font-medium`}
                      style={{
                        width: `${KEY_WIDTH}px`,
                        height: `${KEY_HEIGHT}px`,
                        minWidth: `${KEY_WIDTH}px`,
                        maxWidth: `${KEY_WIDTH}px`,
                      }}
                    >
                      {time === "0000" ? (
                        <button
                          className="transform pb-1 text-lg hover:text-indigo-600 focus:outline-none"
                          onClick={handleHideEarlySegments}
                          aria-label="Hide early morning times"
                          type="button"
                        >
                          &hellip;
                        </button>
                      ) : null}
                      {showHourBubble && (
                        <span
                          className="absolute left-1/2 -translate-x-1/2 -top-[10px] z-10 rounded-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-sm"
                          aria-hidden
                        >
                          {time}
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className="bg-gray-50 dark:bg-slate-800 h-16">&nbsp;</div>
              </div>
            </div>
            <div
              className="overflow-x-auto overflow-y-hidden"
              style={{ width: `calc(100% - ${KEY_WIDTH}px)` }}
              onScroll={handleGridScroll}
              data-testid="room-schedule-horizontal-scroller"
            >
              <div
                data-testid="room-schedule-data-column"
                className="w-full min-w-[200px]"
                style={{ width: contentWidthPx ? `${contentWidthPx}px` : undefined }}
              >
                <div className="h-full flex flex-col relative w-full">
                  <div data-testid="room-schedule-day-schedule" className="flex relative w-full">
                    <div className="w-full flex flex-col border-l border-dotted border-gray-200 dark:border-slate-800">
                      {segments.map((time, segIdx) => {
                        if (hiddenEarly && TIMES_TO_HIDE_EARLY.includes(time)) {
                          return null;
                        }
                        const isHalfHour = time.endsWith("30");
                        const borderClasses = isHalfHour
                          ? "border-b border-gray-400 dark:border-slate-500"
                          : "border-b border-gray-200 dark:border-slate-700 border-dotted";
                        return (
                          <div
                            key={segIdx}
                            data-time={time}
                            className={`${borderClasses} flex items-center justify-center text-xs bg-white dark:bg-slate-800`}
                            style={{
                              height: `${KEY_HEIGHT}px`,
                              minHeight: `${KEY_HEIGHT}px`,
                              maxHeight: `${KEY_HEIGHT}px`,
                            }}
                          >
                            &nbsp;
                          </div>
                        );
                      })}
                      <div className="border-r h-full absolute z-20 w-full">
                        <div className="flex flex-row h-full justify-evenly gap-1">
                          {/* Room booking columns */}
                          {visibleRooms.map((room) => {
                            const _bg =
                              getPaletteBySwatch(room.swatch)?.transparent || "bg-gray-100/10 dark:bg-slate-700";
                            const visibleSegments = hiddenEarly
                              ? segments.filter((t) => !TIMES_TO_HIDE_EARLY.includes(t))
                              : segments;
                            // occursOnDate handles every case (non-recurring, explicit recurrence,
                            // slot fallback for bookings whose `recurrence` document is null/missing).
                            // See lib/recurrence.ts and the WBDTXB regression for the bug write-up.
                            const roomBookings =
                              bookings?.filter((b) => {
                                if (b.roomId !== room.id) return false;
                                if (b.isCancelled) return false;
                                const currentDate = date ?? new Date();
                                return occursOnDate(b, currentDate);
                              }) || [];

                            return (
                              <div className={`flex-1 ${_bg} h-full rounded overflow-hidden relative`} key={room.id}>
                                {/* Render clickable time segments for this room */}
                                <div className="absolute inset-0 flex flex-col">
                                  {visibleSegments.map((time, segIdx) => {
                                    const slotShuttered = date ? isTimeSlotShuttered(room, date, time) : false;
                                    return (
                                      <div
                                        key={`${room.id}-${time}-${segIdx}`}
                                        data-time={time}
                                        data-room-id={room.id}
                                        // Stable selector for e2e: deterministically click a known room×time
                                        // slot to open MakeABookingDialog. e.g. time-slot-orange-0900.
                                        data-testid={`time-slot-${room.id}-${time}`}
                                        className={`border-b border-gray-200 dark:border-slate-700 border-dotted pointer-events-auto relative ${slotShuttered ? "cursor-not-allowed bg-gray-300/40 dark:bg-slate-800/60" : "hover:bg-amber-100 dark:hover:bg-indigo-950 hover:cursor-pointer"}`}
                                        style={{
                                          height: `${KEY_HEIGHT}px`,
                                          minHeight: `${KEY_HEIGHT}px`,
                                          maxHeight: `${KEY_HEIGHT}px`,
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTimeSlotClick(e, time, room);
                                        }}
                                      >
                                        {slotShuttered && (
                                          <div
                                            className="absolute inset-0 pointer-events-none"
                                            style={{
                                              backgroundImage:
                                                "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.06) 10px, rgba(0,0,0,0.04) 25px)",
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Render bookings for this room on this day */}
                                {roomBookings.map((booking) => {
                                  // Compute visible segments for correct top if hiddenEarly is enabled
                                  const visibleSegments = hiddenEarly
                                    ? segments.filter((t) => !(hiddenEarly && TIMES_TO_HIDE_EARLY.includes(t)))
                                    : segments;

                                  // Shared placement: a midnight end ("0000") reads as 24:00 and the grid runs to 24:00.
                                  const tile = layoutBookingTile(booking.startTime, booking.endTime, visibleSegments, hiddenEarly, KEY_HEIGHT);
                                  if (!tile) return null;
                                  const { top: _top, height: _height, isClippedAtStart, isClippedAtEnd } = tile;
                                  const half = parseInt((KEY_HEIGHT / 2).toString(), 10);

                                  return (
                                    <div key={booking.id || booking.startTime}>
                                      {hasAnyBuffer(booking) && !isClippedAtStart && !isClippedAtEnd && (
                                        <Buffers
                                          booking={booking}
                                          top={_top}
                                          height={_height}
                                          half={half}
                                          member={member}
                                        />
                                      )}
                                      <Bubble
                                        booking={booking}
                                        member={member}
                                        top={_top}
                                        height={_height}
                                        allowClick
                                        locationSlug={selectedLocation?.slug}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                          {/* Amenity booking columns */}
                          {selectedAmenities.map((amenity) => {
                            const amenityBookingsForDay = amenityBookings.filter((ab) => {
                              if (ab.amenityId !== amenity.id || !ab.date) return false;
                              const currentDate = date ?? new Date();
                              // Amenity bookings don't have recurrence, just use simple date comparison
                              // Wrap ab.date in new Date() since it comes from JSON as a string
                              return isSameDay(new Date(ab.date), currentDate);
                            });

                            return (
                              <AmenityBookingColumn
                                key={amenity.id}
                                amenity={amenity}
                                amenityBookings={amenityBookingsForDay}
                                date={date ?? new Date()}
                                segments={segments}
                                hiddenEarly={hiddenEarly}
                                timesToHideEarly={TIMES_TO_HIDE_EARLY}
                                keyHeight={KEY_HEIGHT}
                                member={member}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div data-testid="room-schedule-day-footer" className="flex h-16">
                    <div
                      className={`pt-4.5 border-r border-b bg-gray-50 border-gray-200 border-dotted text-center ${
                        isToday ? "!tpr-background px-1 rounded-sm text-white" : ""
                      }`}
                      style={{
                        width: `${dynamicDayWidth()}px`,
                        minWidth: `${dynamicDayWidth()}px`,
                        maxWidth: `${dynamicDayWidth()}px`,
                      }}
                      data-testid={`room-schedule-day-header-${format(date ?? new Date(), "yyyy-MM-dd")}`}
                    >
                      {formatDayHeader(date ?? new Date())}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {roomForInfoModal && selectedLocation && (
        <div className="h-0 hidden invisible">
          <RoomDialog
            open={showRoomInfoModal}
            room={roomForInfoModal}
            location={selectedLocation}
            onOpenChange={setShowRoomInfoModal}
          />
        </div>
      )}

      {/* <CreateBookingDialog
        showBookingDialog={showBookingDialog}
        setShowBookingDialog={setShowBookingDialog}
        bookingResult={bookingResult}
        onBookingCreated={handleBookingSuccess}
      /> */}

      <MakeABookingDialog
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        bookingResult={bookingResult}
        onBookingCreated={refreshBookings}
      />

      {/* <SuccessfulBookingDialog
        showSuccessDialog={showBookingSuccessDialog}
        setShowSuccessDialog={setShowBookingSuccessDialog}
        createdBooking={createdBooking}
        onClose={refreshBookings}
      /> */}
    </>
  );
};

export default BrickDayScheduleAlt;
